import { useEffect, useMemo, useState } from 'react';
import { PageLayout } from 'shared-ui';

import { ReactComponent as CollapseIcon } from 'assets/icons/collapse.svg';
import { ReactComponent as ExpandIcon } from 'assets/icons/expand.svg';
import { Loader } from 'ui/loader/Loader';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { DefinitionsBar } from '../definitionsBar/DefinitionsBar';
import { OrganizationHeader } from '../organization/organizationHeader/OrganizationHeader';
import { EXPAND_ORGANIZATION_ITEM_TAGS } from '../organization/Organization.variables';

import * as Styled from './Zones.styles';
import { ZonesProps } from './Zones.types';
import { ZoneItem } from './zoneItem/ZoneItem';
import { getAllZoneCodes, getExpandableZoneIds, getZoneAncestorIds, toggleZoneSubtree } from './Zones.utils';

export const Zones = ({ zones, isLoading, isModifyDisabled, isMonitoringActive }: ZonesProps) => {
  const { formatMessage } = useLocale();
  const [expanded, setExpanded] = useState<string[]>([]);
  const [isInitiallyExpanded, setIsInitiallyExpanded] = useState(false);
  const [selectedZoneId, setSelectedZoneId] = useState<string>();
  const [searchZoneId, setSearchZoneId] = useState<string>();

  const expandableZoneIds = useMemo(() => getExpandableZoneIds(zones), [zones]);
  const allZoneCodes = useMemo(() => getAllZoneCodes(zones), [zones]);
  const isAllNodesExpanded = expandableZoneIds.length > 0 && expandableZoneIds.every((id) => expanded.includes(id));

  useEffect(() => {
    if (isInitiallyExpanded || !zones.length) return;

    setExpanded(expandableZoneIds);
    setIsInitiallyExpanded(true);
  }, [zones]);

  useEffect(() => {
    if (selectedZoneId && !getZoneAncestorIds(zones, selectedZoneId)) {
      setSelectedZoneId(undefined);
    }
  }, [zones]);

  // Search does not filter the tree: it opens only the branch of the found zone and selects it.
  useEffect(() => {
    if (!searchZoneId) {
      setSelectedZoneId(undefined);
      return;
    }

    const ancestorIds = getZoneAncestorIds(zones, searchZoneId);
    if (!ancestorIds) return;

    setSelectedZoneId(searchZoneId);
    setExpanded(ancestorIds);
  }, [searchZoneId]);

  const handleToggle = (event: React.SyntheticEvent, nodeIds: string[]) => {
    if (EXPAND_ORGANIZATION_ITEM_TAGS.includes((event.target as HTMLElement)?.tagName)) {
      setExpanded(toggleZoneSubtree(zones, expanded, nodeIds));
    }
  };

  const handleToggleAll = () => {
    setExpanded(isAllNodesExpanded ? [] : expandableZoneIds);
  };

  const handleSelectZone = (zoneId: string) => {
    setSelectedZoneId(selectedZoneId === zoneId ? undefined : zoneId);
  };

  const handleExpandZone = (zoneId: string) => {
    setExpanded((prevExpanded) => (prevExpanded.includes(zoneId) ? prevExpanded : [...prevExpanded, zoneId]));
  };

  return (
    <PageLayout
      header={formatMessage({ id: AppMessages['definitions.title'] })}
      renderContent={() => (
        <>
          <DefinitionsBar zones={zones} searchZoneId={searchZoneId} setSearchZoneId={setSearchZoneId} />
          {isLoading ? (
            <Loader />
          ) : (
            <>
              <OrganizationHeader isAllNodesExpanded={isAllNodesExpanded} handleToggleAll={handleToggleAll} />
              <Styled.TreeView
                selected={selectedZoneId || ''}
                expanded={expanded}
                aria-label="zones-label"
                defaultCollapseIcon={<ExpandIcon />}
                defaultExpandIcon={<CollapseIcon />}
                onNodeToggle={handleToggle}
              >
                {zones.map((zone) => (
                  <ZoneItem
                    key={zone.id}
                    zone={zone}
                    allZoneCodes={allZoneCodes}
                    isModifyDisabled={isModifyDisabled}
                    isMonitoringActive={isMonitoringActive}
                    onSelectZone={handleSelectZone}
                    onExpandZone={handleExpandZone}
                  />
                ))}
              </Styled.TreeView>
            </>
          )}
        </>
      )}
    />
  );
};
